import {h, cloneElement} from 'preact'
import {
  createContext,
  useState,
  useReducer,
  useContext,
  useMemo,
  useEffect,
} from 'preact/compat'
import css from 'minify-css.macro'
import JSON5 from 'json5'
import {Column, Grid} from '@dash-ui/react-layout'
import useLayoutEffect from '@react-hook/passive-layout-effect'
import {Tabs, TabList, Tab, Panel, useIsActive} from '@accessible/tabs'
import {useThemes} from '@dash-ui/react'
import {useDependency} from '../hooks'
import {styles} from '../styles'
import {Button} from './Button'
import {Text} from './Text'
import {Spinner} from './Spinner'
import {ReactPreview} from './ReactPreview'
import type {GridProps} from '@dash-ui/react-layout'
import type {UseCacheStatus} from '@react-hook/cache'
import type {IAceEditorProps} from 'react-ace'

const statusMap = ['error', 'idle', 'loading', 'cancelled', 'success']

export const Editor: React.FC<EditorProps> = ({
  language = 'jsx',
  height = {phone: 600, desktop: 400},
  tabs = {},
  active,
  defaultActive = 0,
  ...props
}) => {
  const [AceEditor, ace] = useDependency('react-ace')
  const [{status, previewCode}, dispatch] = useReducer(
    (state, action: EditorAction) => {
      switch (action.type) {
        case 'setStatus':
          return {
            ...state,
            status:
              statusMap[
                Math.min(
                  ...action.value.map((s: UseCacheStatus) =>
                    statusMap.indexOf(s)
                  )
                )
              ],
          }
        case 'updatePreview':
          return {...state, previewCode: action.value}
      }

      return state
    },
    {
      previewCode: '',
      status: statusMap[Math.min(statusMap.indexOf(ace.status))],
    }
  )

  useDependency.preload('buble')

  // Updates whenever the status of our imported dependencies change
  useEffect(() => {
    dispatch({
      type: 'setStatus',
      value: [ace.status],
    })
  }, [ace.status])

  const context = useMemo<EditorContextType>(
    () => ({
      editor: AceEditor,
      language,
      updatePreview: (value) => dispatch({type: 'updatePreview', value}),
    }),
    [AceEditor, language]
  )

  const tabsChildren: JSX.Element[] = Object.keys(tabs).map((name) => (
    <Tab key={name} inactiveClass={tab('inactive')} activeClass={tab('active')}>
      <span>{name}</span>
    </Tab>
  ))

  return status === 'error' ? (
    // Error
    <Column gap='lg'>
      <Text>Failed to load the editor</Text>
      <Button
        onClick={() => {
          ace.status === 'error' && ace.retry()
        }}
      >
        Retry
      </Button>
    </Column>
  ) : status !== 'success' || status !== 'success' ? (
    // Idle | Loading
    <Spinner size={36} />
  ) : (
    // Success
    <EditorContext.Provider value={context}>
      <Grid
        className={editor()}
        cols={{phone: 1, desktop: previewCode ? 2 : 1}}
        rows={{phone: 2, tablet: 1}}
        height={height}
        {...props}
      >
        <Grid rows={['auto', '1fr']} style={{minHeight: 320}}>
          <Tabs active={active} defaultActive={defaultActive}>
            <TabList>
              <Grid cols={tabsChildren.length} className={tabList()}>
                {tabsChildren}
              </Grid>
            </TabList>
            <div>
              {Object.values(tabs).map((editorPanel, i) =>
                cloneElement(editorPanel, {key: i, index: i})
              )}
            </div>
          </Tabs>
        </Grid>

        {previewCode && <ReactPreview code={previewCode} scope={{styles}} />}
      </Grid>
    </EditorContext.Provider>
  )
}

export type EditorAction =
  | {
      type: 'setStatus'
      value: UseCacheStatus[]
    }
  | {
      type: 'updatePreview'
      value: string
    }

const EditorContext = createContext<EditorContextType>({
  editor: null,
  language: 'jsx',
  updatePreview: () => '',
})

interface EditorContextType {
  editor: null | React.ComponentType<IAceEditorProps>
  language: EditorProps['language']
  updatePreview: (value: string) => void
}

const useEditor = () => useContext(EditorContext)

export const EditorPanel: React.FC<EditorPanelProps> = ({
  index,
  name,
  value,
  preview,
  getPreview = (code) => code,
  onChange = () => {},
}) => {
  const {editor: AceEditor, language, updatePreview} = useEditor()
  const active = useIsActive(index)

  useLayoutEffect(() => {
    if (preview && active) {
      updatePreview(getPreview(value))
    }
    // eslint-disable-next-line
  }, [updatePreview, preview, active, value])

  return (
    <Panel
      index={index}
      inactiveClass={panel('inactive')}
      activeClass={panel('active')}
    >
      {AceEditor && (
        <AceEditor
          name={name}
          mode={language}
          tabSize={2}
          fontSize={13}
          theme='nord_dark'
          keyboardHandler='vscode'
          debounceChangePeriod={1000}
          width='100%'
          height='100%'
          className={highlight()}
          value={value}
          onChange={onChange}
        />
      )}
    </Panel>
  )
}

interface EditorPanelProps {
  index?: number
  name: string
  value: string
  preview?: boolean
  getPreview?: (code: string) => string
  onChange?: (value: string, event: any) => void
}

export const StylesPanel: React.FC<StylesPanelProps> = ({
  defaultStyles = '',
  ...props
}) => {
  const [code, setCode] = useState(defaultStyles)
  return (
    <EditorPanel
      name='styles'
      value={code}
      getPreview={(code) => code}
      onChange={setCode}
      {...props}
    />
  )
}

interface StylesPanelProps extends Omit<EditorPanelProps, 'value' | 'name'> {
  defaultStyles?: string
  name?: string
}

export const VariablesPanel: React.FC<VariablesPanelProps> = ({
  defaultVariables = {},
  ...props
}) => {
  const [variables, setVariables] = useState<string>(
    JSON5.stringify(defaultVariables, null, 2)
  )
  useThemes({sandbox: useMemo(() => JSON5.parse(variables), [variables])})

  return (
    <EditorPanel
      name='variables'
      value={`styles.variables(${variables})`}
      onChange={(variables) =>
        setVariables(variables.replace('styles.variables(', '').slice(0, -1))
      }
      {...props}
    />
  )
}

interface VariablesPanelProps extends Omit<EditorPanelProps, 'value' | 'name'> {
  defaultVariables?: Record<string, any>
  name?: string
}

const highlight = styles({
  default: ({font}) => css`
    width: 100%;
    font-family: ${font.family.mono};
    font-weight: 400;
    text-align: left;
    background-color: #2e3440;
  `,
})

const editor = styles.one(
  ({radius, elevation}) => css`
    border-radius: ${radius.primary};
    overflow: hidden;
    box-shadow: ${elevation.floating};
    background-color: #2e3440;
  `
)

const tabList = styles.one`
  & > * + * {
    border-left: 1px solid rgba(255, 255, 255, 0.2);
  }
`

const tab = styles({
  default: ({pad, font, transition}) => css`
    padding: ${pad.sm};
    background-color: #1b1e24;
    font-family: ${font.family.brand};
    font-size: ${font.size.contentSm};
    line-height: ${font.leading.contentSm};
    letter-spacing: ${font.tracking.contentSm};
    font-weight: 700;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    transition-property: background-color, color;
    transition-duration: ${transition.duration.primary};
    transition-timing-function: ${transition.timing.move};

    &:hover {
      cursor: pointer;
      user-select: none;
    }
  `,
  inactive: ({color}) => css`
    color: rgba(255, 255, 255, 0.5);

    &:hover {
      color: ${color['intention/info']};
    }
  `,
  active: ({color}) => css`
    background-color: #2e3440;
    color: ${color['intention/info']};
  `,
})

const panel = styles({
  default: ({elevation, transition}) => css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: auto;
    box-shadow: ${elevation.inset};
    transition: transform, opacity, visibility;
    transition-duration: ${transition.duration.primary};
    transition-timing-function: ${transition.timing.decelerate};
  `,
  inactive: css`
    transform: translateY(100%);
    opacity: 0;
  `,
  active: css`
    transform: translateY(0);
    opacity: 1;
  `,
})

export interface EditorProps extends GridProps {
  language?: 'jsx' | 'tsx' | 'typescript' | 'javascript'
  active?: number
  defaultActive?: number
  tabs: {
    [name: string]: React.ReactElement<EditorPanelProps> | JSX.Element
  }
}
